import {
    $isTextNode,
    DOMConversion,
    DOMConversionOutput,
    TextNode,
    LexicalEditor,
    TextFormatType
  } from 'lexical';

// Solution idea from discussion: 
// https://github.com/facebook/lexical/issues/2452#issuecomment-1337549564

const patchStyleConversion = (
  originalDOMConverter?: (node: HTMLElement) => DOMConversion | null,
): ((node: HTMLElement) => DOMConversionOutput | null) => {
  return node => {
      const original = originalDOMConverter?.(node);
      if (!original) {
          return null;
      }

      const originalOutput = original.conversion(node);

      if (!originalOutput) {
          return originalOutput;
      }

      const backgroundColor = node.style.backgroundColor;
      const color = node.style.color;
      const height = node.style.height;
      const width = node.style.width;

      return {
          ...originalOutput,
          forChild: (lexicalNode, parent) => {
              const originalForChild = originalOutput?.forChild ?? (x => x);
              const result = originalForChild(lexicalNode, parent);
              if ($isTextNode(result)) {
                  const style = [
                      backgroundColor ? `background-color: ${backgroundColor}` : null,
                      color ? `color: ${color}` : null,
                      height ? `height: ${height}` : null,
                      width ? `width: ${width}` : null,
                  ]
                      .filter((value) => value != null)
                      .join('; ');
                  if (style.length) {
                      return result.setStyle(style);
                  }
              }
              return result;
          },
      };
  };
};

export function applyHtmlToRichContentPatches() {
  const importers = TextNode.importDOM();
  TextNode.importDOM = function _() {
      return {
          ...importers,
          span: () => ({
            conversion: patchStyleConversion(importers?.span),
            priority: 0,
          }),
          strong: () => ({
            conversion: patchStyleConversion(importers?.strong),
            priority: 0,
          }),
      };
  };

  const missedFormatTag: Array<[TextFormatType, string]> = [
      ['underline', 'u'],
      ['strikethrough', 's'],
  ];

  const exportDOM = TextNode.prototype.exportDOM;
  TextNode.prototype.exportDOM = function _(this: TextNode, editor: LexicalEditor) {
      const { element } = exportDOM.apply(this, [editor]);
      if (!element) {
          return { element };
      }

      let wrapped = element;

      for (const [format, tag] of missedFormatTag) {
          if ($hasFormat(this, format)) {
              const wrapper = document.createElement(tag);
              wrapper.appendChild(element);
              wrapped = wrapper;
          }
      }

      return { element: wrapped };
  };
}

function $hasFormat(node: TextNode, format: TextFormatType): boolean {
    const currentFormat = node.getFormat();
    return node.getFormatFlags(format, null) < currentFormat;
}